import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { Panel as RFPanel } from "@xyflow/react";
import { useAtomValue } from "jotai";
import { IconButton, Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  nodeAtomFamily,
  NodeType,
  sideBarSelectedAtom,
  sideBarWidthAtom,
  determineAssetType,
  suggestedNodeAtomFamily,
  DefaultNodeData,
  useCopilotDrawerState,
  capitalizedAssetTypeDisplayAtom,
} from "@zapier/canvas-state";

import { getIconNameByAssetType } from "../../../utils/getIconNameByAssetType";
import { AgentPreview } from "../../AgentPreview";
import { ZapPreview } from "./ZapPreview";
import { TablePreview } from "./TablePreview";
import { InterfacePreview } from "./InterfacePreview";
import styles from "./DraftAssetPreview.module.css";

interface RightPanelProps {
  onClose: () => void;
  closeOnEsc?: boolean;
  nodeId: string;
}

const assetContentMap = {
  zap: ZapPreview,
  table: TablePreview,
  interface: InterfacePreview,
  agent: AgentPreview,
};

export const DraftAssetPreview = ({
  onClose,
  closeOnEsc = true,
  nodeId,
}: RightPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  const node = useAtomValue(nodeAtomFamily(nodeId));
  const suggestedNode = useAtomValue(suggestedNodeAtomFamily(nodeId));
  const nodeOrSuggestedNode = node ?? suggestedNode;
  const isSideBarOpen = useAtomValue(sideBarSelectedAtom);
  const sideBarWidth = useAtomValue(sideBarWidthAtom);
  const [isCopilotOpen] = useCopilotDrawerState();
  const getAssetDisplayName = useAtomValue(capitalizedAssetTypeDisplayAtom);
  /* If this component is open for a draft node that gets built,
   * the component may reference a node ID no longer in nodesAtom.
   * We now close the component on ZSL success, but this is an extra safeguard.
   */
  if (!nodeOrSuggestedNode) {
    return null;
  }

  const assetType = determineAssetType(
    (nodeOrSuggestedNode.data as DefaultNodeData).services,
    nodeOrSuggestedNode.type as NodeType,
  );

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (closeOnEsc && e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, [onClose, closeOnEsc]);

  if (!assetType || assetType === "chatbot") {
    return null;
  }

  const Content = assetContentMap[assetType];
  const previewContent = <Content nodeId={nodeId} />;

  const content = (
    <RFPanel
      className={classNames(styles.rightPanelWrapper, {
        [styles.sidebarOpen]: isSideBarOpen || isCopilotOpen,
      })}
      style={
        {
          "--sidebar-width": `${sideBarWidth}px`,
        } as React.CSSProperties
      }
    >
      <div ref={panelRef} className={styles.rightPanel}>
        <div className={styles.rightPanelHeader}>
          <Icon
            name={getIconNameByAssetType(assetType)}
            size="small"
            color="BrandOrange"
          />
          <span className={styles.rightPanelTitle}>
            <Text type="ParagraphHeader3">
              {getAssetDisplayName(assetType)}
            </Text>
          </span>
          <IconButton
            name="formX"
            onPress={onClose}
            variant="ghost"
            size="medium"
          />
        </div>
        <div
          className={classNames(styles.rightPanelContent, {
            [styles.transparentBackground]: assetType === "agent",
          })}
        >
          {previewContent}
        </div>
      </div>
    </RFPanel>
  );

  // Create portal to attach to the end of the body
  return createPortal(content, document.body);
};
